import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X, Copy, FileText, Download, Trash2, Clock, Users, MessageSquare, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loading } from "@/components/shared";
import api, { API } from "@/lib/api";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const fileUrl = (u) => (u?.startsWith("http") ? u : `${API.replace(/\/api$/, "")}${u}`);

function Pill({ active, onClick, label, count, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-2 rounded-md px-3.5 py-2 text-sm font-semibold transition-colors ${active ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100"}`}>
      {label} {count != null && <span className={`rounded px-1.5 text-xs tabular-nums ${active ? "bg-white/20" : "bg-slate-200 text-slate-600"}`}>{count}</span>}
    </button>
  );
}

function ProofView({ app }) {
  const proof = app.proof;
  if (!proof || app.proof_deleted) return <p className="text-sm text-slate-400">No proof on file{app.proof_deleted ? " (deleted)" : ""}.</p>;
  const isPdf = (proof.content_type || "").includes("pdf");
  const url = fileUrl(proof.url);
  return (
    <div>
      {isPdf ? (
        <iframe src={url} title="proof" className="h-72 w-full rounded-md border border-slate-200" data-testid="proof-iframe" />
      ) : (
        <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="proof" className="max-h-72 rounded-md border border-slate-200" data-testid="proof-image" /></a>
      )}
      <a href={url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald hover:underline">
        <FileText className="h-3.5 w-3.5" /> Open {proof.filename}
      </a>
    </div>
  );
}

function ApplicationCard({ app, onAction }) {
  const [approveResult, setApproveResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/table/admin/applications/${app.id}/approve`, { origin_url: window.location.origin });
      setApproveResult(data);
      onAction();
    } catch { toast.error("Failed to approve"); } finally { setBusy(false); }
  };
  const reject = async () => {
    if (!window.confirm("Reject this application? A polite email will be sent.")) return;
    setBusy(true);
    try { await api.post(`/table/admin/applications/${app.id}/reject`); toast.success("Rejected"); onAction(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const delProof = async () => {
    if (!window.confirm("Delete the uploaded proof file? This cannot be undone.")) return;
    try { await api.delete(`/table/admin/applications/${app.id}/proof`); toast.success("Proof deleted"); onAction(); }
    catch { toast.error("Failed"); }
  };
  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied"); };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`table-app-${app.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="font-heading text-lg font-semibold text-navy">{app.name} <span className="text-slate-400">·</span> {app.business_name}</h3>
          <p className="mt-1 text-sm text-slate-500">{app.category} · {app.turnover_band} · {app.city} · {app.years_in_business || "—"} yrs</p>
          <p className="mt-1 text-sm text-slate-500">{app.email} · {app.whatsapp} · {app.website && <a href={app.website.startsWith("http") ? app.website : `https://${app.website}`} target="_blank" rel="noreferrer" className="text-emerald hover:underline">{app.website}</a>}</p>
        </div>
        <span className="rounded bg-slate-100 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-slate-600">{app.status}</span>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div className="space-y-4">
          <div><p className="text-xs font-bold uppercase tracking-wider text-slate-400">Stuck on (90 days)</p><p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{app.stuck_on}</p></div>
          <div><p className="text-xs font-bold uppercase tracking-wider text-slate-400">Referral / how they found it</p><p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{app.referral}</p></div>
          <div><p className="text-xs font-bold uppercase tracking-wider text-slate-400">Ownership</p><p className="mt-1.5 text-sm text-slate-700 tabular-nums">{app.ownership_pct}%</p></div>
        </div>
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Turnover proof</p>
            {app.proof && !app.proof_deleted && <Button variant="ghost" size="sm" onClick={delProof} data-testid={`del-proof-${app.id}`} className="h-7 text-red-600 hover:bg-red-50"><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete</Button>}
          </div>
          <ProofView app={app} />
        </div>
      </div>

      {approveResult && (
        <div className="mt-5 rounded-lg border border-emerald/30 bg-emerald/5 p-4" data-testid={`approve-result-${app.id}`}>
          <p className="text-sm font-semibold text-navy">Approved — {inr(approveResult.amount)} ({approveResult.tier})</p>
          <div className="mt-3 flex items-center gap-2">
            <Input readOnly value={approveResult.payment_link} className="h-9 text-xs" data-testid={`pay-link-${app.id}`} />
            <Button size="sm" variant="outline" onClick={() => copy(approveResult.payment_link)} className="h-9"><Copy className="h-3.5 w-3.5" /></Button>
          </div>
          <Button size="sm" variant="outline" onClick={() => copy(approveResult.whatsapp_message)} className="mt-2 h-9" data-testid={`copy-wa-${app.id}`}><Copy className="mr-1.5 h-3.5 w-3.5" /> Copy WhatsApp message</Button>
        </div>
      )}

      {app.status === "pending" && (
        <div className="mt-5 flex gap-3 border-t border-slate-100 pt-5">
          <Button onClick={approve} disabled={busy} className="bg-emerald text-white hover:bg-emerald-700" data-testid={`approve-${app.id}`}><Check className="mr-1.5 h-4 w-4" /> Approve</Button>
          <Button onClick={reject} disabled={busy} variant="outline" className="text-red-600 hover:bg-red-50" data-testid={`reject-${app.id}`}><X className="mr-1.5 h-4 w-4" /> Reject</Button>
        </div>
      )}
      {app.status === "approved" && !approveResult && (
        <div className="mt-5 border-t border-slate-100 pt-5">
          <Button onClick={approve} disabled={busy} variant="outline" size="sm" data-testid={`resend-${app.id}`}>Regenerate payment link</Button>
        </div>
      )}
    </div>
  );
}

function ApplicationsTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const { data: counts } = useQuery({ queryKey: ["table-counts"], queryFn: async () => (await api.get("/table/admin/counts")).data });
  const { data, isLoading } = useQuery({ queryKey: ["table-apps", status], queryFn: async () => (await api.get(`/table/admin/applications?status=${status}`)).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["table-apps"] }); qc.invalidateQueries({ queryKey: ["table-counts"] }); qc.invalidateQueries({ queryKey: ["table-members"] }); };
  return (
    <div>
      <div className="mb-6 flex flex-wrap gap-1">
        <Pill active={status === "pending"} onClick={() => setStatus("pending")} label="Pending" count={counts?.pending} testid="tab-app-pending" />
        <Pill active={status === "approved"} onClick={() => setStatus("approved")} label="Approved" count={counts?.approved} testid="tab-app-approved" />
        <Pill active={status === "paid"} onClick={() => setStatus("paid")} label="Paid members" count={counts?.paid} testid="tab-app-paid" />
        <Pill active={status === "rejected"} onClick={() => setStatus("rejected")} label="Rejected" count={counts?.rejected} testid="tab-app-rejected" />
      </div>
      {isLoading ? <Loading /> : (data || []).length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-400">No {status} applications.</div>
      ) : (
        <div className="space-y-4">{(data || []).map((a) => <ApplicationCard key={a.id} app={a} onAction={refresh} />)}</div>
      )}
    </div>
  );
}

function MembersTab() {
  const { data, isLoading } = useQuery({ queryKey: ["table-members"], queryFn: async () => (await api.get("/table/admin/members")).data });
  const [q, setQ] = useState("");
  const rows = (data || []).filter((m) => !q || `${m.name} ${m.business_name} ${m.city} ${m.category}`.toLowerCase().includes(q.toLowerCase()));
  const exportCsv = async () => {
    try { const res = await api.get("/table/admin/members.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "owners-table-members.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  if (isLoading) return <Loading />;
  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <Input placeholder="Search members…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 max-w-xs" data-testid="member-search" />
        <Button variant="outline" onClick={exportCsv} data-testid="member-csv"><Download className="mr-1.5 h-4 w-4" /> CSV</Button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="table-members-table">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>{["Name", "Business", "Category", "Turnover", "City", "Tier", "Paid", "Joined", "Renews"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((m) => (
              <tr key={m.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-navy">{m.name}</td>
                <td className="px-4 py-3 text-slate-600">{m.business_name}</td>
                <td className="px-4 py-3 text-slate-600">{m.category}</td>
                <td className="px-4 py-3 text-slate-600">{m.turnover_band}</td>
                <td className="px-4 py-3 text-slate-600">{m.city}</td>
                <td className="px-4 py-3"><span className={`rounded px-2 py-0.5 text-xs font-bold uppercase ${m.tier === "founding" ? "bg-gold/20 text-gold-dark" : "bg-slate-100 text-slate-600"}`}>{m.tier}</span></td>
                <td className="px-4 py-3 text-slate-600 tabular-nums">{inr(m.amount_paid)}</td>
                <td className="px-4 py-3 text-slate-600 tabular-nums">{(m.joined_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-slate-600 tabular-nums">{(m.renews_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No members yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StuckTab() {
  const { data, isLoading } = useQuery({ queryKey: ["table-stuck"], queryFn: async () => (await api.get("/table/admin/stuck-answers")).data });
  if (isLoading) return <Loading />;
  return (
    <div>
      <p className="mb-4 text-sm text-slate-500">Every answer to “what have you been stuck on” — your content pipeline. Newest first.</p>
      <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1" data-testid="stuck-feed">
        {(data || []).map((s, i) => (
          <div key={i} className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{s.business_name} · {s.category} · {s.turnover_band}</p>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{s.stuck_on}</p>
          </div>
        ))}
        {(data || []).length === 0 && <div className="rounded-xl border border-dashed border-slate-300 py-12 text-center text-slate-400">No answers yet.</div>}
      </div>
    </div>
  );
}

function SettingsTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["table-settings"], queryFn: async () => (await api.get("/table/admin/settings")).data });
  const [f, setF] = useState(null);
  React.useEffect(() => { if (data) setF(data); }, [data]);
  if (isLoading || !f) return <Loading />;
  const save = async () => {
    try {
      await api.put("/table/admin/settings", {
        founding_cap: Number(f.founding_cap), founding_price: Number(f.founding_price),
        standard_price: Number(f.standard_price), renewal_price: Number(f.renewal_price),
        annual_table_price: Number(f.annual_table_price), whatsapp_invite_link: f.whatsapp_invite_link || "",
      });
      qc.invalidateQueries({ queryKey: ["table-settings"] }); qc.invalidateQueries({ queryKey: ["table-stats"] });
      toast.success("Settings saved");
    } catch { toast.error("Failed to save"); }
  };
  const F = ({ k, label, prefix }) => (
    <div><Label className="text-xs">{label}</Label><div className="mt-1 flex items-center">{prefix && <span className="mr-1 text-slate-400">{prefix}</span>}<Input value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`setting-${k}`} /></div></div>
  );
  return (
    <div className="max-w-xl space-y-4 rounded-xl border border-slate-200 bg-white p-6" data-testid="table-settings-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <F k="founding_cap" label="Founding seat cap" />
        <F k="founding_price" label="Founding price" prefix="₹" />
        <F k="standard_price" label="Standard price" prefix="₹" />
        <F k="renewal_price" label="Renewal price" prefix="₹" />
        <F k="annual_table_price" label="Annual Table ticket" prefix="₹" />
      </div>
      <F k="whatsapp_invite_link" label="WhatsApp community invite link" />
      <Button onClick={save} className="bg-navy text-white hover:bg-navysurface" data-testid="table-settings-save">Save settings</Button>
      <p className="border-t border-slate-100 pt-4 text-xs leading-relaxed text-slate-400">
        Razorpay (India payments) is currently disabled. To enable live/sandbox card payments, set
        <span className="font-mono text-slate-500"> RAZORPAY_KEY_ID</span> and
        <span className="font-mono text-slate-500"> RAZORPAY_KEY_SECRET</span> in <span className="font-mono">/app/backend/.env</span> and restart the backend. Until then, approved applicants get a payment link that shows the amount and holds their seat.
      </p>
    </div>
  );
}

export default function OwnersTableAdmin() {
  const [tab, setTab] = useState("applications");
  return (
    <div data-testid="owners-table-admin">
      <div className="mb-6 flex flex-wrap gap-1 border-b border-slate-200 pb-3">
        <Pill active={tab === "applications"} onClick={() => setTab("applications")} label="Applications" testid="ot-tab-applications" />
        <Pill active={tab === "members"} onClick={() => setTab("members")} label="Members" testid="ot-tab-members" />
        <Pill active={tab === "stuck"} onClick={() => setTab("stuck")} label="Stuck answers" testid="ot-tab-stuck" />
        <Pill active={tab === "settings"} onClick={() => setTab("settings")} label="Settings" testid="ot-tab-settings" />
      </div>
      {tab === "applications" && <ApplicationsTab />}
      {tab === "members" && <MembersTab />}
      {tab === "stuck" && <StuckTab />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}
